import React from 'react'
import { useDispatch } from 'react-redux'
import type { KambiEvent, KambiSearchTermsView } from '@mobi/api-types/kambi'
import { List, ListItem } from '@mobi/component-library/Base/List'
import { OverlayClose } from '@core/Components/Overlay'
import { addSearchTermToPrevious } from '@core/Areas/Search/Store'
import { NoSearchResults } from '../Common/NoSearchResults'
import { SearchErrored } from '../Common/SearchErrored'
import { EmptyScreen } from '../Common/EmptyScreen'
import { LoadingSkeleton } from '../Common/LoadingSkeleton'
import { useNavigate } from 'react-router'
import { ResultContentListRow } from '../Common/ResultContentListRow'
import { SportsIcon } from '@mobi/sports-offering/Components/Common/SportsIcon'

export const SportsSearch: React.FC<{
  isError: boolean
  isLoading: boolean
  searchTerm: string
  handleImmediateSearch: (searchTerm: string) => void
  searchResults?: KambiSearchTermsView
}> = ({ isError, isLoading, searchResults, searchTerm, handleImmediateSearch }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  if (isError) {
    return <SearchErrored />
  }
  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (!searchResults) {
    return <EmptyScreen searchMode='sports' handleImmediateSearch={handleImmediateSearch} />
  }

  if (searchResults.resultTerms.length === 0) {
    return <NoSearchResults />
  }

  const handleResultClick = (
    searchResult: KambiSearchTermsView['resultTerms']['0'],
    event?: KambiEvent
  ) => {
    dispatch(addSearchTermToPrevious({ type: 'sports', searchTerm }))
    OverlayClose()
    if (searchResult.type === 'PARTICIPANT' && event) {
      const path = event.path.map(x => x.termKey).join('/')
      navigate(
        `/sports/${path}/event/${event.id}?participant=${encodeURIComponent(searchResult.localizedName)}`
      )
    } else if (searchResult.type === 'REGION' && searchResult.parentId) {
      navigate('/sports' + searchResult.parentId)
    } else {
      navigate('/sports' + searchResult.id)
    }
  }

  return (
    <List variant='card' itemSize='dynamic'>
      {searchResults.resultTerms.map(searchResult => {
        return (
          <ListItem
            key={searchResult.id}
            onClick={() => handleResultClick(searchResult)}
            testId='search-result'
            icon={<SportsIcon sport={searchResult.icon} />}
            content={
              <ResultContentListRow
                searchTerm={searchResults.term}
                primaryText={searchResult.value}
                secondaryText={searchResult.secondaryValue}
              />
            }
          />
        )
      })}
    </List>
  )
}
